import { AssignmentSubmission } from '@/features/course/learner/types/assignment';
import {
  MyLearningActivityAssignmentSubmissionsDocument,
  RLearningActivityAssignmentSubmissionFragment,
  RMyLearningActivityAssignmentSubmissionsQuery,
  useMyLearningActivityAssignmentSubmissionsQueryRemote,
  useSubmitLearningActivityAssignmentMutationRemote,
} from '@/store/v2';

const toAssignmentSubmission = (remote: RLearningActivityAssignmentSubmissionFragment): AssignmentSubmission => ({
  file: { name: remote.fileName, url: remote.fileURL.url },
  // A replacement overwrites the submission, so the last replace is what the learner submitted
  submittedAt: remote.updatedAt,
});

type UseAssignmentSubmissionResult = {
  submission: AssignmentSubmission | null;
  /** Only the first load — a background refetch keeps showing what is already there. */
  loading: boolean;
  /** Submits the file, replacing any earlier submission. Rejects when the server refuses it. */
  submitFile: (file: File) => Promise<AssignmentSubmission>;
  submitting: boolean;
};

export const useAssignmentSubmission = (learningActivityId: number): UseAssignmentSubmissionResult => {
  const variables = { input: { learningActivityIdsIN: [learningActivityId] } };

  // The file URL is presigned and short-lived, so a cached one may already have expired by the
  // time the learner comes back to the activity.
  const { data, loading } = useMyLearningActivityAssignmentSubmissionsQueryRemote({
    variables,
    fetchPolicy: 'cache-and-network',
    skip: !learningActivityId,
  });
  const [submitAssignment, { loading: submitting }] = useSubmitLearningActivityAssignmentMutationRemote();

  const remoteSubmission = data?.myLearningActivityAssignmentSubmissions.find(
    (submission) => submission.learningActivityId === learningActivityId,
  );

  const submitFile = async (file: File): Promise<AssignmentSubmission> => {
    const { data: submitted } = await submitAssignment({
      variables: { input: { learningActivityId, file } },
      // Submissions have no id for the cache to merge them by, so the query is rewritten with
      // the server's answer. That also carries a fresh presigned URL for View.
      update: (cache, { data: result }) => {
        if (!result) return;

        cache.writeQuery<RMyLearningActivityAssignmentSubmissionsQuery>({
          query: MyLearningActivityAssignmentSubmissionsDocument,
          variables,
          data: { myLearningActivityAssignmentSubmissions: [result.submitLearningActivityAssignment] },
        });
      },
    });

    // errorPolicy 'all' resolves a refused submission instead of rejecting it
    if (!submitted?.submitLearningActivityAssignment) {
      throw new Error('The assignment submission was not accepted');
    }

    return toAssignmentSubmission(submitted.submitLearningActivityAssignment);
  };

  return {
    submission: remoteSubmission ? toAssignmentSubmission(remoteSubmission) : null,
    loading: loading && !data,
    submitFile,
    submitting,
  };
};
